// ===========================================
// PRINTER SETUP
// ===========================================
// One-time per device: point the POS at the LAN thermal printers
// (ESC/POS, port 9100 — Epson, Star, and most generics). A test print
// confirms each one before service.

import { useState } from 'react';
import { Printer, X, Check, Loader2 } from 'lucide-react';
import { printerService } from '../services/printer';
import { restaurantApi } from '../services/api';
import { useToast } from './Toast';

interface PrinterSettingsModalProps {
  onClose: () => void;
}

export default function PrinterSettingsModal({ onClose }: PrinterSettingsModalProps) {
  const { showSuccess, showError } = useToast();
  const initial = printerService.getConfig();
  const [enabled, setEnabled] = useState(initial.type === 'tcp');
  const [receiptIp, setReceiptIp] = useState(initial.receiptIp || '');
  const [kitchenIp, setKitchenIp] = useState(initial.kitchenIp || '');
  const [testing, setTesting] = useState<'receipt' | 'kitchen' | null>(null);

  const save = async () => {
    printerService.configure({
      type: enabled && receiptIp.trim() ? 'tcp' : 'browser',
      receiptIp: receiptIp.trim() || undefined,
      kitchenIp: kitchenIp.trim() || undefined,
    });
    // Share with every POS device (restaurant-wide setting; managers can also
    // set this from the dashboard, Settings -> Devices).
    const res = await restaurantApi.updateSettings({
      printers: {
        receiptIp: enabled ? receiptIp.trim() || null : null,
        kitchenIp: enabled ? kitchenIp.trim() || null : null,
      },
    });
    if (res.success) {
      showSuccess('Printer setup saved for all POS devices.');
    } else {
      showSuccess('Saved on this device. (Could not sync to other devices — a manager can set it in the dashboard.)');
    }
    onClose();
  };

  const test = async (which: 'receipt' | 'kitchen') => {
    const ip = (which === 'receipt' ? receiptIp : kitchenIp).trim();
    if (!ip) return;
    setTesting(which);
    try {
      await printerService.testPrint(ip);
      showSuccess('Test sent — check the printer.');
    } catch (e: any) {
      showError(e?.message || "Couldn't reach the printer.");
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl max-h-[90dvh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Printer className="w-5 h-5 text-primary-600" />
            Printers
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-slate-900">Print to thermal printers</p>
              <p className="text-xs text-slate-500">ESC/POS over the restaurant's wifi (port 9100)</p>
            </div>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-5 h-5 accent-primary-600"
            />
          </label>

          {enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Receipt printer IP</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={receiptIp}
                    onChange={(e) => setReceiptIp(e.target.value)}
                    placeholder="192.168.1.50"
                    className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    onClick={() => test('receipt')}
                    disabled={!receiptIp.trim() || testing !== null}
                    className="px-3 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium disabled:opacity-40"
                  >
                    {testing === 'receipt' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Kitchen printer IP <span className="text-slate-400 font-normal">(optional — receipts printer used if empty)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={kitchenIp}
                    onChange={(e) => setKitchenIp(e.target.value)}
                    placeholder="192.168.1.51"
                    className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    onClick={() => test('kitchen')}
                    disabled={!kitchenIp.trim() || testing !== null}
                    className="px-3 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium disabled:opacity-40"
                  >
                    {testing === 'kitchen' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                  </button>
                </div>
              </div>

              <p className="text-xs text-slate-400">
                Find the printer's IP on its self-test page (hold the feed button
                while powering on) — and give it a static IP on the router so it
                never changes.
              </p>
            </>
          )}

          <button
            onClick={save}
            className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
